import React from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';
import scrollbar from '../../styles/scrollbar';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

// Keys, not words. A phase the server names but this table does not know
// still falls through to the raw phase, exactly as it always did.
const PHASE_LABELS = {
  LOBBY: 'menu.phase.lobby',
  REVEAL: 'menu.phase.reveal',
  BIDDING: 'menu.phase.bidding',
  DEMO: 'menu.phase.demo',
  SOLVED: 'menu.phase.solved',
  FAILED: 'menu.phase.failed',
  SKIP: 'menu.phase.skipped',
  GAME_OVER: 'menu.phase.finished',
};

const useStyles = createUseStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    // Sized for the modal it now lives in, not the card strip it used to be.
    maxHeight: 'min(52vh, 420px)',
    overflowY: 'auto',
    ...scrollbar,
  },
  empty: {
    fontFamily: MONO,
    fontSize: '11px',
    color: '#8a6e4c',
    textAlign: 'center',
    padding: '18px 8px',
    lineHeight: 1.6,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    minHeight: '46px',
    padding: '8px 12px',
    boxSizing: 'border-box',
    background: '#1c130c',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: '#e8d6ae',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
    transition: 'background .12s, border-color .12s',
    '&:hover': { background: '#26190f', borderColor: '#6b4622' },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
  },
  code: {
    flex: 1,
    minWidth: 0,
    fontFamily: MONO,
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#f5e6c8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  lock: { fontSize: '12px', flexShrink: 0, lineHeight: 1 },
  badge: {
    fontFamily: MONO,
    fontSize: '8.5px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: '#e35c36',
    border: '1px solid #7d2a12',
    borderRadius: '4px',
    padding: '2px 5px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  phase: {
    fontFamily: MONO,
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#a8875d',
    flexShrink: 0,
  },
  players: {
    fontFamily: MONO,
    fontSize: '11.5px',
    color: '#c4a070',
    flexShrink: 0,
    minWidth: '26px',
    textAlign: 'right',
  },
});

/**
 * The live rooms, as pressed-tin rows.
 *
 * A room that is not accepting players is badged but stays CLICKABLE: the menu
 * cannot know whether you hold a seat in one, and a player rejoining after a
 * refresh must be able to click their own game. Greying the row out would lock
 * them out of it — the gate on the Game route explains the refusal to everyone
 * else. One gate, covering every path in.
 */
function RoomList({ rooms, onPick }) {
  const classes = useStyles();
  const t = useT();

  if (!rooms.length) {
    return <div className={classes.empty}>{t('menu.noRooms')}</div>;
  }

  return (
    <div className={classes.list}>
      {rooms.map(r => {
        const locked = r.visibility === 'password';
        const phaseKey = PHASE_LABELS[r.phase];
        const phase = phaseKey ? t(phaseKey) : r.phase;
        const label = [
          r.gameId,
          locked ? t('a11y.passwordProtected') : null,
          r.joinable ? null : t('a11y.notAcceptingNewPlayers'),
          phase,
          t('menu.playerCount', { count: r.players }),
        ].filter(Boolean).join(', ');

        return (
          <button
            key={r.gameId}
            type="button"
            className={classes.row}
            onClick={() => onPick(r.gameId)}
            aria-label={label}
          >
            <span className={classes.code}>{r.gameId}</span>
            {locked && <span className={classes.lock} aria-hidden="true">🔒</span>}
            {!r.joinable && <span className={classes.badge}>{t('access.noEntry')}</span>}
            <span className={classes.phase}>{phase}</span>
            <span className={classes.players}>{r.players} 👤</span>
          </button>
        );
      })}
    </div>
  );
}

export default RoomList;
